import React, { useCallback, useEffect, useState } from "react"
import {
  InputLabel,
  Input,
  makeStyles,
  Typography,
  Button,
  ButtonGroup,
} from "@material-ui/core"
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date"
import dayjs, { Dayjs } from "dayjs"
import { colors } from "../../utils/theme"

const useStyles = makeStyles((theme) => ({
  inputContainer: {
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
    gap: theme.spacing(1),
  },
  inputLabelContainer: {
    display: "flex",
    flexDirection: "column",
  },
  inputLabel: {
    fontSize: ".8rem",
    padding: "0.3rem 0.2rem",
    color: colors.neutral.cement,
  },
  timeInput: {
    width: "100%",
    fontSize: "1.5rem",
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: "3px",
    padding: ".3rem",
    "& input": {
      textAlign: "center",
    },
    "&.Mui-focused": {
      background: colors.primary.ice_03,
    },
  },
  colon: {
    fontSize: "2rem",
    fontWeight: "bold",
  },
  amPmButton: {
    minWidth: 30,
    minHeight: 24,
    padding: theme.spacing(0.25, 0.5),
    textTransform: "none",
    fontWeight: "bold",
    fontSize: "0.75rem",
  },
  selectedButton: {
    backgroundColor: colors.primary.periwinkle,
    color: colors.neutral.white,
    "&:hover": {
      backgroundColor: colors.primary.shadow,
    },
  },
  unselectedButton: {
    backgroundColor: colors.primary.ice,
    color: colors.primary.midnight,
    "&:hover": {
      backgroundColor: colors.primary.mist,
    },
  },
}))

interface TimePickerProps {
  label: string
  defaultTime: Dayjs
  value?: Dayjs | MaterialUiPickersDate
  onTimeChange: (time: Dayjs | MaterialUiPickersDate) => void
}

/**
 * TimePicker
 *
 * A compact HH:MM time input with an AM/PM toggle.
 * - Displays two numeric inputs (hour and minute) separated by a colon.
 * - Enforces 12-hour clock semantics (hour 1–12, minute 0–59).
 * - Toggling AM/PM updates the internal time and triggers validation.
 * - When a valid time is entered, calls `onTimeChange` with a Dayjs instance.
 * - When the input is invalid or incomplete, calls `onTimeChange` with `null`.
 *
 * Controlled usage: Provide `value` to reflect external state
 */
export default function TimePicker({
  label,
  defaultTime,
  value,
  onTimeChange,
}: TimePickerProps) {
  const classes = useStyles()
  const [amPm, setAmPm] = useState<"am" | "pm">(
    defaultTime.format("a") as "am" | "pm"
  )
  const [hour, setHour] = useState<string>(defaultTime.format("h"))
  const [minute, setMinute] = useState<string>(defaultTime.format("mm"))

  // Optionally use as controlled component
  useEffect(() => {
    if (value) {
      const next = dayjs(value)
      setAmPm(next.format("a") as "am" | "pm")
      setHour(next.format("h"))
      setMinute(next.format("mm"))
    }
  }, [value])

  /**
   * Validates hour/minute/period and emits an updated time.
   *
   * Uses strict Dayjs parsing with the format "h:mm A". If the constructed
   * time is valid, forwards a Dayjs object via `onTimeChange`. Otherwise,
   * emits `null` to indicate an invalid or incomplete state.
   */
  const validateAndSetTime = useCallback(
    (h: string, m: string, period: "am" | "pm") => {
      if (!h || !m) return

      const dayjsTime = dayjs(
        `${h}:${m.padStart(2, "0")} ${period.toUpperCase()}`,
        "h:mm A",
        true
      )

      if (dayjsTime.isValid()) {
        onTimeChange(dayjsTime)
      } else {
        onTimeChange(null)
      }
    },
    [onTimeChange]
  )

  // Make sure we call the give onChange function with the default time
  useEffect(() => {
    validateAndSetTime(hour, minute, amPm)
  }, [validateAndSetTime, hour, minute, amPm])

  /**
   * Handles numeric input for hour/minute fields with clamping.
   *
   * - Non-numeric input is ignored.
   * - Values above `max` are reduced to the last digit.
   * - Values within [min, max] are accepted as-is.
   * - After updating the relevant field, triggers `validateAndSetTime`.
   *
   * @param value Raw string from the input field
   * @param min Inclusive minimum acceptable value
   * @param max Inclusive maximum acceptable value
   * @param setter State setter for the target field (hour/minute)
   * @param field Identifies which field is being updated
   */
  const updateTimeField = (
    value: string,
    min: number,
    max: number,
    setter: React.Dispatch<React.SetStateAction<string>>,
    field: "hour" | "minute"
  ) => {
    const num = Number(value)
    if (Number.isNaN(num)) return

    let newValue = value
    if (num > max) {
      newValue = String(num % 10)
      setter(newValue)
    } else if (num >= min && num <= max) {
      newValue = String(num)
      setter(newValue)
    }
  }

  const handleAmPmToggle = (period: "am" | "pm") => {
    setAmPm(period)
    validateAndSetTime(hour, minute, period)
  }

  return (
    <div>
      <InputLabel style={{ marginBottom: "0.3rem" }}>{label}</InputLabel>
      <div className={classes.inputContainer}>
        <div className={classes.inputLabelContainer}>
          <Input
            className={classes.timeInput}
            value={hour.padStart(2, "0")}
            onChange={(e) =>
              updateTimeField(e.target.value, 1, 12, setHour, "hour")
            }
            onFocus={(e) => {
              const input = e.target
              const length = input.value.length
              input.setSelectionRange(length, length)
            }}
            disableUnderline
            inputProps={{
              placeholder: "HH",
              "aria-label": "Hours",
            }}
          />
          <InputLabel className={classes.inputLabel}>Hour</InputLabel>
        </div>
        <Typography className={classes.colon}>:</Typography>
        <div className={classes.inputLabelContainer}>
          <Input
            className={classes.timeInput}
            value={minute.padStart(2, "0")}
            onChange={(e) =>
              updateTimeField(e.target.value, 0, 59, setMinute, "minute")
            }
            onFocus={(e) => {
              const input = e.target
              const length = input.value.length
              input.setSelectionRange(length, length)
            }}
            disableUnderline
            inputProps={{
              placeholder: "MM",
              "aria-label": "Minutes",
            }}
          />
          <InputLabel className={classes.inputLabel}>Minute</InputLabel>
        </div>
        <ButtonGroup
          orientation="vertical"
          variant="contained"
          aria-label="AM PM toggle"
        >
          <Button
            className={`${classes.amPmButton} ${
              amPm === "am" ? classes.selectedButton : classes.unselectedButton
            }`}
            onClick={() => handleAmPmToggle("am")}
          >
            AM
          </Button>
          <Button
            className={`${classes.amPmButton} ${
              amPm === "pm" ? classes.selectedButton : classes.unselectedButton
            }`}
            onClick={() => handleAmPmToggle("pm")}
          >
            PM
          </Button>
        </ButtonGroup>
      </div>
    </div>
  )
}
